import { HARD_BREAK_KEY, HARD_BREAK_SCHEMA } from '@adornis/prosemirror-editor/nodes/hard-break/schema.js';
import { PARAGRAPH_KEY, PARAGRAPH_SCHEMA } from '@adornis/prosemirror-editor/nodes/paragraph/schema.js';
import { TEXT_KEY, TEXT_SCHEMA } from '@adornis/prosemirror-editor/nodes/text/schema.js';
import {
  WEBSITE_COMPONENT_KEY,
  WEBSITE_COMPONENT_SCHEMA,
  WEBSITE_DOCUMENT_SCHEMA,
} from '../../schema.js';
import { baseKeymap } from 'prosemirror-commands';
import { history, redo, undo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { Schema, type Mark, type MarkSpec, type Node as ProseMirrorNode } from 'prosemirror-model';
import { EditorState, TextSelection, type Transaction } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import {
  normalizeInlineTextStyleList,
  type WebEditorInlineTextStyle,
} from '../../inline-typography.js';

const INLINE_STYLE_MARK = 'vivaInlineStyle';

const inlineStyleMark = (): MarkSpec => ({
  attrs: {
    fontWeight: { default: null },
    fontSizeDesktop: { default: null },
    fontSizeMobile: { default: null },
    color: { default: null },
    highlight: { default: null },
  },
  inclusive: true,
  parseDOM: [
    {
      tag: 'span[data-viva-inline-style]',
      getAttrs: element => {
        if (!(element instanceof HTMLElement)) return false;
        const numeric = (name: string) => {
          const value = Number(element.dataset[name]);
          return Number.isFinite(value) ? value : null;
        };
        return {
          fontWeight: numeric('fontWeight'),
          fontSizeDesktop: numeric('fontSizeDesktop'),
          fontSizeMobile: numeric('fontSizeMobile'),
          color: element.dataset.color || null,
          highlight: element.dataset.highlight === 'true' ? true : null,
        };
      },
    },
  ],
  toDOM: mark => {
    const attrs = mark.attrs as Record<string, unknown>;
    const style = [
      typeof attrs.fontWeight === 'number' ? `font-weight:${attrs.fontWeight}` : '',
      typeof attrs.fontSizeDesktop === 'number' ? `--vp-inline-desktop:${attrs.fontSizeDesktop}px` : '',
      typeof attrs.fontSizeMobile === 'number' ? `--vp-inline-mobile:${attrs.fontSizeMobile}px` : '',
      typeof attrs.color === 'string' ? `color:${attrs.color}` : '',
    ]
      .filter(Boolean)
      .join(';');
    return [
      'span',
      {
        'data-viva-inline-style': '',
        'data-font-weight': attrs.fontWeight ?? '',
        'data-font-size-desktop': attrs.fontSizeDesktop ?? '',
        'data-font-size-mobile': attrs.fontSizeMobile ?? '',
        'data-color': attrs.color ?? '',
        'data-highlight': attrs.highlight === true ? 'true' : '',
        class: attrs.highlight === true ? 'vp-inline-text-highlight' : '',
        style,
      },
      0,
    ];
  },
});

let cachedSchema: Schema | undefined;
const editorSchema = () => {
  cachedSchema ??= new Schema({
    nodes: {
      doc: WEBSITE_DOCUMENT_SCHEMA(),
      [PARAGRAPH_KEY]: PARAGRAPH_SCHEMA(),
      [TEXT_KEY]: TEXT_SCHEMA(),
      [HARD_BREAK_KEY]: HARD_BREAK_SCHEMA(),
      [WEBSITE_COMPONENT_KEY]: WEBSITE_COMPONENT_SCHEMA(),
    },
    marks: { [INLINE_STYLE_MARK]: inlineStyleMark() },
  });
  return cachedSchema;
};

const textMarks = (schema: Schema, style: WebEditorInlineTextStyle | undefined) => {
  if (!style) return [];
  return [
    schema.marks[INLINE_STYLE_MARK]?.create({
      fontWeight: style.fontWeight ?? null,
      fontSizeDesktop: style.fontSizeDesktop ?? null,
      fontSizeMobile: style.fontSizeMobile ?? null,
      color: style.color ?? null,
      highlight: style.highlight === true ? true : null,
    }),
  ].filter((mark): mark is Mark => Boolean(mark));
};

const paragraphContent = (
  schema: Schema,
  value: string,
  paragraphStart: number,
  styles: WebEditorInlineTextStyle[],
) => {
  if (!value) return undefined;
  const paragraphEnd = paragraphStart + value.length;
  const boundaries = new Set([paragraphStart, paragraphEnd]);
  styles.forEach(style => {
    if (style.end <= paragraphStart || style.start >= paragraphEnd) return;
    boundaries.add(Math.max(paragraphStart, style.start));
    boundaries.add(Math.min(paragraphEnd, style.end));
  });
  const offsets = [...boundaries].sort((left, right) => left - right);
  return offsets.slice(0, -1).flatMap((start, index) => {
    const end = offsets[index + 1];
    if (end === undefined || start >= end) return [];
    const text = value.slice(start - paragraphStart, end - paragraphStart);
    const style = styles.find(candidate => candidate.start <= start && candidate.end >= end);
    return [schema.text(text, textMarks(schema, style))];
  });
};

const createDocument = (value: string, styles: WebEditorInlineTextStyle[]) => {
  const schema = editorSchema();
  let offset = 0;
  const paragraphs = value.split('\n').map(text => {
    const content = paragraphContent(schema, text, offset, styles);
    offset += text.length + 1;
    return schema.nodes.paragraph?.create(null, content);
  });
  const component = schema.nodes.websiteComponent?.create(
    {
      id: 'web-inline-text',
      componentType: 'web-text',
      componentVersion: 1,
      props: {},
      styles: {},
      responsive: {},
      hidden: false,
      locked: true,
    },
    paragraphs.filter((node): node is ProseMirrorNode => Boolean(node)),
  );
  if (!component) throw new Error('Das ProseMirror-Schema enthält keine Website-Komponente.');
  return schema.nodes.doc?.create(
    { formatVersion: 1, projectId: 'web-inline' },
    component,
  ) ?? schema.topNodeType.create(null, component);
};

export interface ExtractedWebInlineText {
  value: string;
  styles: WebEditorInlineTextStyle[];
}

const styleFromMark = (mark: Mark) => {
  if (mark.type.name !== INLINE_STYLE_MARK) return null;
  const attrs = mark.attrs as Record<string, unknown>;
  return {
    ...(typeof attrs.fontWeight === 'number' ? { fontWeight: attrs.fontWeight } : {}),
    ...(typeof attrs.fontSizeDesktop === 'number' ? { fontSizeDesktop: attrs.fontSizeDesktop } : {}),
    ...(typeof attrs.fontSizeMobile === 'number' ? { fontSizeMobile: attrs.fontSizeMobile } : {}),
    ...(typeof attrs.color === 'string' ? { color: attrs.color } : {}),
    ...(attrs.highlight === true ? { highlight: true } : {}),
  };
};

const extractDocument = (doc: ProseMirrorNode): ExtractedWebInlineText => {
  const paragraphs: Array<{ value: string; styles: WebEditorInlineTextStyle[] }> = [];
  const component = doc.firstChild;
  component?.forEach(paragraph => {
    let value = '';
    const styles: WebEditorInlineTextStyle[] = [];
    paragraph.descendants(node => {
      if (!node.isText || !node.text) return;
      const start = value.length;
      value += node.text;
      const inlineStyle = node.marks.map(styleFromMark).find(Boolean);
      if (inlineStyle) styles.push({ start, end: value.length, ...inlineStyle });
    });
    paragraphs.push({ value, styles });
  });

  let offset = 0;
  const styles = paragraphs.flatMap((paragraph, index) => {
    const mapped = paragraph.styles.map(style => ({ ...style, start: style.start + offset, end: style.end + offset }));
    offset += paragraph.value.length + (index < paragraphs.length - 1 ? 1 : 0);
    return mapped;
  });
  const value = paragraphs.map(paragraph => paragraph.value).join('\n');
  return { value, styles: normalizeInlineTextStyleList(styles, value.length) };
};

const textOffset = (doc: ProseMirrorNode, position: number) => doc.textBetween(0, position, '\n').length;

export interface WebProseMirrorInlineEditorOptions {
  elementId: string;
  sessionKey?: string;
  value: string;
  styles: WebEditorInlineTextStyle[];
  multiline: boolean;
  toolbarId: string;
  onCommit: (result: ExtractedWebInlineText) => void;
  onCancel: () => void;
  onSelectionChange: (editor: HTMLElement, value: string, start: number, end: number) => void;
}

interface MountedEditor {
  view: EditorView;
  options: WebProseMirrorInlineEditorOptions;
  blur: (event: FocusEvent) => void;
  ignoreNextBlur?: boolean;
}

const mountedEditors = new WeakMap<HTMLElement, MountedEditor>();

const syncEditorDataset = (element: HTMLElement, view: EditorView, options: WebProseMirrorInlineEditorOptions) => {
  const extracted = extractDocument(view.state.doc);
  element.dataset.value = extracted.value;
  const start = textOffset(view.state.doc, view.state.selection.from);
  const end = textOffset(view.state.doc, view.state.selection.to);
  options.onSelectionChange(element, extracted.value, start, end);
};

export const mountWebProseMirrorInlineEditor = (
  element: HTMLElement,
  options: WebProseMirrorInlineEditorOptions,
) => {
  const mounted = mountedEditors.get(element);
  if (mounted?.options.elementId === options.elementId && mounted.options.sessionKey === options.sessionKey) {
    mounted.options = options;
    syncEditorDataset(element, mounted.view, options);
    return;
  }
  if (mounted) {
    element.removeEventListener('focusout', mounted.blur);
    mounted.view.destroy();
  }

  const state = EditorState.create({
    schema: editorSchema(),
    doc: createDocument(options.value, options.styles),
    plugins: [
      history(),
      keymap({
        'Mod-z': undo,
        'Mod-y': redo,
        'Mod-Shift-z': redo,
        Escape: () => {
          options.onCancel();
          return true;
        },
        'Mod-Enter': currentState => {
          options.onCommit(extractDocument(currentState.doc));
          return true;
        },
        Enter: currentState => {
          if (options.multiline) return false;
          options.onCommit(extractDocument(currentState.doc));
          return true;
        },
        ...baseKeymap,
      }),
    ],
  });

  let view: EditorView;
  const dispatchTransaction = (transaction: Transaction) => {
    const nextState = view.state.apply(transaction);
    view.updateState(nextState);
    syncEditorDataset(element, view, mountedEditors.get(element)?.options ?? options);
  };
  view = new EditorView(element, {
    state,
    dispatchTransaction,
    nodeViews: {
      websiteComponent: () => {
        const contentDOM = document.createElement('div');
        contentDOM.className = 'vp-editor-prosemirror-content';
        return { dom: contentDOM, contentDOM };
      },
    },
    attributes: {
      class: 'vp-editor-prosemirror-surface',
      spellcheck: 'true',
      'aria-label': 'Text direkt bearbeiten',
    },
  });

  const blur = (event: FocusEvent) => {
    const related = event.relatedTarget as HTMLElement | null;
    if (related?.closest('.vp-editor-inline-selection-toolbar')) return;
    window.setTimeout(() => {
      const current = mountedEditors.get(element);
      if (!current || element.contains(document.activeElement)) return;
      if (current.ignoreNextBlur) {
        current.ignoreNextBlur = false;
        return;
      }
      current.options.onCommit(extractDocument(current.view.state.doc));
    }, 0);
  };
  element.addEventListener('focusout', blur);
  mountedEditors.set(element, { view, options, blur });
  syncEditorDataset(element, view, options);
  window.requestAnimationFrame(() => {
    const end = Math.max(1, view.state.doc.content.size - 1);
    view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(end))));
    view.focus();
  });
};

export const readWebProseMirrorInlineValue = () =>
  document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror')?.dataset.value;

export const readActiveWebProseMirrorInlineResult = (): ExtractedWebInlineText | null => {
  const element = document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror');
  const mounted = element ? mountedEditors.get(element) : undefined;
  if (!mounted) return null;
  mounted.ignoreNextBlur = true;
  return extractDocument(mounted.view.state.doc);
};

export const suppressNextWebProseMirrorBlurCommit = () => {
  const element = document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror');
  const mounted = element ? mountedEditors.get(element) : undefined;
  if (mounted) mounted.ignoreNextBlur = true;
};

export const commitActiveWebProseMirrorInlineEditor = () => {
  const element = document.querySelector<HTMLElement>('.vp-editor-inline-prosemirror');
  const mounted = element ? mountedEditors.get(element) : undefined;
  if (!mounted) return false;
  mounted.ignoreNextBlur = true;
  mounted.options.onCommit(extractDocument(mounted.view.state.doc));
  return true;
};
